import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { forkJoin } from "rxjs";
import { SubSink } from "subsink";
import { NotificationMiddlewareService } from "../../shared/services/notifications/notifications-middleware.service";
import { Language } from "../../shared/services/pcode/pcode.models";
import { PCodeService } from "../../shared/services/pcode/pcode.service";
import { LoginService } from "../login/login.service";
import { TFASetup, UserProfile, UserProfileForm } from "./settings.models";
import { WebAppSettingsService } from "./settings.service";
import { HttpErrorResponse } from "@angular/common/http";
import { MatDialog } from "@angular/material/dialog";
import { TFADialogComponent } from "../../shared/components/tfa-dialog/tfa-dialog.component";
import { NoopScrollStrategy } from "@angular/cdk/overlay";

@Component({
	selector: 'webapp-settings',
	templateUrl: './settings.component.html',
	styleUrl: './settings.component.scss'
})
export class WebAppSettingsComponent implements OnInit, OnDestroy
{
	// subsink
	private subs = new SubSink();

	// variables
	public user: UserProfile = null;
	public languages: Language[];

	// two factor authentication
	public showTFAConfiguration = false;
	public userTFA: TFASetup = null;

	// language form
	public form = this.fb.group<UserProfileForm>(
		{
			languageCode: this.fb.control(null)
		}
	);

	// costructor
	constructor
		(
			private settingsService: WebAppSettingsService,
			private loginService: LoginService,
			private pcodeService: PCodeService,
			private pushNotificationsMiddleware: NotificationMiddlewareService,
			private translateService: TranslateService,
			private fb: FormBuilder,
			private router: Router,
			private dialog: MatDialog
		)
	{

	}

	// init
	ngOnInit()
	{
		const getLanguagesObs = this.pcodeService.getLanguages();
		const getUserProfileObs = this.settingsService.getLoggedUserProfile();

		this.subs.sink = forkJoin([getLanguagesObs, getUserProfileObs]).subscribe(results =>
		{
			this.languages = results[0];
			this.user = results[1];

			this.form.get('languageCode').setValue(this.translateService.currentLang);

			// get user TFA configuration
			this.getUserTFA();
		});
	}

	// switch language
	switchLanguage()
	{
		const languageAngular = this.form.get('languageCode').value;

		// update logged user language
		this.settingsService.updateLanguage(languageAngular).subscribe(() =>
		{
			// update system language
			this.pcodeService.changeLanguage(languageAngular);

			// form
			this.form.get('languageCode').setValue(languageAngular);
		});
	}

	// on destroy
	ngOnDestroy()
	{
		// unsubscribe from sink
		this.subs.unsubscribe();
	}

	// toggle push notification
	togglePushNotifications(enable: boolean)
	{
		if (enable)
			this.enablePushNotifications();
		else
			this.disablePushNotifications();
	}

	// enable push notifications
	enablePushNotifications()
	{
		this.pushNotificationsMiddleware.requestSubscription()
			.then(subscription =>
			{
				this.subs.sink = this.pushNotificationsMiddleware.subscribeUser(subscription).subscribe(() =>
				{
					this.user.pushNotifications = true;
					this.getPushSubscriptionStatus(subscription);
				});
			})
			.catch(() =>
			{
				this.user.pushNotifications = false;
				this.pcodeService.notify('error', this.translateService.instant('Modules.Settings.PushNotifications.ErrorMessage'));
			});
	}

	// disable push notifications
	disablePushNotifications()
	{
		this.pushNotificationsMiddleware.requestSubscription()
			.then(subscription =>
			{
				if (subscription)
				{
					// unsubscribing from client
					subscription.unsubscribe().then(() =>
					{
						// unsubscribing from server
						this.subs.sink = this.pushNotificationsMiddleware.unsubscribeUser(subscription).subscribe(() =>
						{
							// updating subscription status
							this.user.pushNotifications = false;
						});
					});
				}
			})
			.catch(() =>
			{
				// unsubscribing from server
				this.subs.sink = this.pushNotificationsMiddleware.unsubscribeUser(null).subscribe(() =>
				{
					// updating subscription status
					this.user.pushNotifications = false;
				});
			});
	}

	// get push notifications subscription status
	getPushSubscriptionStatus(subscription)
	{
		const newSubscription = JSON.parse(JSON.stringify(subscription));

		// updating subscription status
		this.subs.sink = this.pushNotificationsMiddleware.getSubscriptionStatus({ endPoint: newSubscription.endpoint, auth: newSubscription.keys.auth, p256Dh: newSubscription.keys.p256dh }).subscribe(result =>
		{
			this.user.pushNotifications = result['status'];
		});
	}

	// logout
	logout()
	{
		// confirm logout
		this.pcodeService.confirm('Attenzione!', `Confermi di voler effettuare il logout dall\'account ${this.user.username}?`).subscribe(result =>
		{
			if (result)
			{
				this.loginService.logout(false);
				this.router.navigate(['/login']);
			}
		});
	}

	// two factor authentication
	getUserTFA()
	{
		this.subs.sink = this.settingsService.getUserTFA().subscribe(response =>
		{
			this.userTFA = response;

			if (this.userTFA.isTFAEnabled)
				this.showUserTFA();
			else
				this.hideUserTFA();
		});
	}
	showUserTFA()
	{
		this.showTFAConfiguration = true;
	}
	hideUserTFA()
	{
		this.showTFAConfiguration = false;
	}
	toggleTFA()
	{
		const dialogRef = this.dialog.open(TFADialogComponent, {
			width: '600px',
			maxWidth: '90vw',
			maxHeight: '90%',
			backdropClass: 'pwa-dialog-overlay-wrapper',
			panelClass: 'pwa-dialog-panel',
			disableClose: true,
			autoFocus: true,
			position: { bottom: '30px' },
			scrollStrategy: new NoopScrollStrategy()
		});

		dialogRef.componentInstance.formSubmitted.subscribe((OTPCode: string) =>
		{
			this.subs.sink = this.settingsService.toggleTFA(OTPCode).subscribe({
				next: (result) =>
				{
					this.getUserTFA();

					if (result.isTFAEnabled)
						this.pcodeService.notify('success', this.translateService.instant('Modules.Profile.Messages.TFAEnableSuccess'));
					else
						this.pcodeService.notify('success', this.translateService.instant('Modules.Profile.Messages.TFADisableSuccess'));
				},
				error: () =>
				{
					this.pcodeService.notify('error', this.translateService.instant('Modules.Profile.Errors.InvalidOtpCode'));
				}
			})
		});
	}
	copyAuthenticatorKey()
	{
		navigator.clipboard.writeText(this.userTFA.authenticatorKey).then(() =>
		{
			this.pcodeService.notify('success', this.translateService.instant('Modules.Profile.Messages.CopyTFAClipboard'));
		})
	}
}